// Agent registry, runs, learnings, scorecards and recommendations (decision D20).
// Agents are data: an agent profile has immutable versions, every run is attributed to one version,
// learnings are scoped (platform → organization → agent → project → agent×project) and reviewed,
// and scorecards/recommendations tell humans when to update, promote, retire or create an agent.
import { z } from "zod";
import { AGENT_KEY_RE, AGENT_SPECIALTY_RE, integrationGrantSchema, type IntegrationGrant } from "./agent-grants.ts";
import { AGENT_ROLES, AGENT_RUNTIMES, LEARNING_KINDS, LEARNING_STATUSES, MEMORY_SCOPES, TASK_TYPES, type AgentRole, type AgentRuntime, type LearningKind, type LearningStatus, type MemoryScope } from "./enums.ts";
import { agentSkillsConfigSchema, type AgentSkillsConfig } from "./skills.ts";

export { AGENT_KEY_RE, AGENT_SPECIALTY_RE, OPERATION_ID_RE, integrationGrantSchema, type IntegrationGrant } from "./agent-grants.ts";
export { agentSkillsConfigSchema, type AgentSkillsConfig } from "./skills.ts";

const id = z.uuid();
const timestamp = z.iso.datetime();

export const AGENT_ORIGINS = ["platform", "organization"] as const;
export type AgentOrigin = (typeof AGENT_ORIGINS)[number];

/** Where a platform template came from (the vendored `@primage/agents` catalog). */
export const catalogRefSchema = z.object({ catalog: z.string().max(100), agent: z.string().regex(AGENT_KEY_RE), version: z.string().max(40) }).strict();
export type CatalogRef = z.infer<typeof catalogRefSchema>;

/** Procedures per version (D41-C12): at most this many; the engine injects at most two per attempt. */
export const AGENT_PROCEDURES_MAX = 8;

/**
 * A procedure: a reviewed how-to packet the builder receives when it applies (when, what to know, the steps, the
 * evidence to cite, the delivery and the done criteria). It is part of the version body, so it is versioned,
 * pinned and hashed with the profile.
 */
export const procedureSchema = z
  .object({
    id: z.string().regex(/^[a-z][a-z0-9-]{1,39}$/),
    version: z.number().int().positive(),
    applies_when: z
      .object({
        task_types: z.array(z.enum(TASK_TYPES)).min(1).max(TASK_TYPES.length).optional(),
        roles: z.array(z.enum(AGENT_ROLES)).min(1).max(AGENT_ROLES.length).optional(),
        /** Matched case-insensitively against the task text (title, description, acceptance criteria). */
        keywords: z.array(z.string().min(2).max(60)).min(1).max(20).optional(),
      })
      .strict(),
    objective: z.string().min(1).max(300),
    knowledge: z.array(z.string().min(1).max(300)).max(8),
    steps: z.array(z.string().min(1).max(300)).min(1).max(8),
    evidence: z.array(z.string().min(1).max(200)).max(6),
    delivery: z.string().min(1).max(300),
    done: z.array(z.string().min(1).max(200)).min(1).max(8),
  })
  .strict();
export type Procedure = z.infer<typeof procedureSchema>;

/** Stored size of a version body, as PostgreSQL checks it (`octet_length(body::text) <= 65536`): the jsonb text adds one space after every ',' and ':'. Counted everywhere, even inside strings, so this is an upper bound. */
export const AGENT_VERSION_BODY_MAX_BYTES = 65_536;
export function agentVersionBodyBytes(body: unknown): number {
  const text = JSON.stringify(body);
  return new TextEncoder().encode(text).length + (text.match(/[,:]/g)?.length ?? 0);
}

const baseAgentVersionShape = {
  role: z.enum(AGENT_ROLES),
  name: z.string().min(1).max(120),
  description: z.string().max(2_000),
  instructions: z.string().max(50_000),
  specialties: z.array(z.string().regex(AGENT_SPECIALTY_RE)).max(20),
  delegates_to: z.array(z.string().regex(AGENT_KEY_RE)).max(20),
  /** Agents this one may consult (read-only questions); defaults to none for versions created before D20 L1b-ii. */
  consults_to: z.array(z.string().regex(AGENT_KEY_RE)).max(20).default([]),
  memory_scopes: z.array(z.enum(MEMORY_SCOPES)).max(5),
  tools: z.array(z.string().max(100)).max(100),
  integration_grants: z.array(integrationGrantSchema).max(50),
  model_target_id: id.nullable(),
  fallback_model_target_ids: z.array(id).max(10),
  context_limit: z.number().int().positive().nullable(),
  output_limit: z.number().int().positive().nullable(),
  budget: z.record(z.string(), z.number().nullable()),
  review_policy: z.record(z.string(), z.string()),
  catalog_ref: catalogRefSchema.nullable(),
};

/**
 * Immutable snapshot of everything that shapes an agent's behavior. Scorecards are computed per
 * version, so a change of instructions, tools, grants, limits, model or skills is a new version.
 * Supports v1 (historical/legacy procedures) and v2 (native skills, D41 / PRD Skills).
 */
export const agentVersionBodySchema = z
  .object({
    ...baseAgentVersionShape,
    schema_version: z.number().int().min(1).max(2).optional(),
    /** Omitted when empty: a version without procedures hashes exactly as before D41-C12. */
    procedures: z.array(procedureSchema).max(AGENT_PROCEDURES_MAX).optional(),
    /** Skills configuration (schema_version 2). */
    skills: agentSkillsConfigSchema.optional(),
  })
  .strict()
  .superRefine((body, ctx) => {
    if (body.schema_version === 2) {
      if (body.procedures && body.procedures.length > 0) {
        ctx.addIssue({ code: "custom", message: "A schema_version: 2 agent cannot declare procedures; use skills.", path: ["procedures"] });
      }
    }
    // Instructions plus procedures/skills must fit the 64 KB check of the version row.
    if (agentVersionBodyBytes(body) > AGENT_VERSION_BODY_MAX_BYTES) {
      ctx.addIssue({ code: "custom", message: `The agent version body exceeds ${AGENT_VERSION_BODY_MAX_BYTES} bytes.`, path: [] });
    }
  });
export type AgentVersionBody = z.infer<typeof agentVersionBodySchema>;

export interface AgentProfileVersion {
  id: string;
  agent_profile_id: string;
  number: number;
  /** sha256 of the canonical JSON of `body`. */
  content_hash: string;
  body: AgentVersionBody;
  change_note: string;
  created_by: string | null;
  created_by_actor?: { type: "user" | "agent" | "service" | "system" | "integration"; id: string | null };
  created_at: string;
}

/** Registry view of an agent profile (the profile row plus registry fields and its current version). */
export interface AgentRegistryEntry {
  id: string;
  project_id: string | null;
  key: string | null;
  name: string;
  role: AgentRole;
  description: string;
  origin: AgentOrigin;
  catalog_ref: CatalogRef | null;
  specialties: string[];
  delegates_to: string[];
  /** From the current version body (read-only consult targets). */
  consults_to: string[];
  memory_scopes: MemoryScope[];
  integration_grants: IntegrationGrant[];
  state: string;
  current_version: { id: string; number: number; content_hash: string } | null;
  /** A newer catalog version exists for this platform template (never applied automatically). */
  catalog_update_available: string | null;
  version: number;
  updated_at: string;
}

export const AGENT_RUN_PURPOSES = ["build", "consult", "delegate", "review", "reflect", "assistant"] as const;
export type AgentRunPurpose = (typeof AGENT_RUN_PURPOSES)[number];
export const AGENT_RUN_OUTCOMES = ["running", "succeeded", "failed", "interrupted", "blocked", "cancelled"] as const;
export type AgentRunOutcome = (typeof AGENT_RUN_OUTCOMES)[number];

export interface AgentRun {
  id: string;
  agent_profile_id: string;
  agent_profile_version_id: string | null;
  runtime: AgentRuntime;
  purpose: AgentRunPurpose;
  task_id: string | null;
  attempt_id: string | null;
  parent_run_id: string | null;
  outcome: AgentRunOutcome;
  steps: number;
  model_calls: number;
  tool_calls: number;
  input_tokens: number;
  output_tokens: number;
  cost_estimated_cents: number | null;
  duration_ms: number | null;
  started_at: string;
  ended_at: string | null;
}

export const LEARNING_TITLE_MAX = 160;
export const LEARNING_BODY_MAX = 4_000;

export interface AgentLearning {
  id: string;
  scope: MemoryScope;
  kind: LearningKind;
  title: string;
  body: string;
  status: LearningStatus;
  /** 0..1 */
  confidence: number;
  project_id: string | null;
  agent_profile_id: string | null;
  source: { kind: "agent_run" | "human" | "knowledge_pack"; run_id: string | null; pack: string | null };
  evidence_refs: string[];
  /** Proposed by a run that consumed untrusted content (web page, e-mail, message): human review required. */
  tainted: boolean;
  uses: { retrieved: number; helpful: number; harmful: number };
  expires_at: string | null;
  supersedes_id: string | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  created_at: string;
  version: number;
}

/** Human-authored learning. Platform scope is only imported from reviewed knowledge packs. */
export const createLearningSchema = z
  .object({
    scope: z.enum(["organization", "project", "agent", "agent_project"]),
    kind: z.enum(LEARNING_KINDS),
    title: z.string().trim().min(3).max(LEARNING_TITLE_MAX),
    body: z.string().trim().min(3).max(LEARNING_BODY_MAX),
    project_id: id.nullable().default(null),
    agent_profile_id: id.nullable().default(null),
    expires_at: timestamp.nullable().default(null),
  })
  .strict();
export type CreateLearningInput = z.infer<typeof createLearningSchema>;

export const reviewLearningSchema = z
  .object({
    decision: z.enum(["approve", "reject", "deprecate"]),
    expected_version: z.number().int().nonnegative(),
    note: z.string().max(1_000).optional(),
  })
  .strict();
export type ReviewLearningInput = z.infer<typeof reviewLearningSchema>;

export const learningListQuerySchema = z.object({
  status: z.enum(LEARNING_STATUSES).optional(),
  scope: z.enum(MEMORY_SCOPES).optional(),
  kind: z.enum(LEARNING_KINDS).optional(),
  project_id: id.optional(),
  agent_profile_id: id.optional(),
  q: z.string().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});

export interface AgentScorecard {
  agent_profile_id: string;
  agent_profile_version_id: string | null;
  window_days: number;
  runs: number;
  successes: number;
  first_pass: number;
  human_interventions: number;
  rework: number;
  success_rate: number | null;
  /** Wilson lower bound (95%) of the success rate: small samples rank low on purpose. */
  success_lower_bound: number | null;
  avg_cost_cents: number | null;
  avg_duration_ms: number | null;
  /** 0..100 composite; null below the minimum sample. */
  score: number | null;
  computed_at: string;
}

export const AGENT_RECOMMENDATION_KINDS = ["update_agent", "create_agent", "promote_version", "retire_agent", "review_learning"] as const;
export type AgentRecommendationKind = (typeof AGENT_RECOMMENDATION_KINDS)[number];

export interface AgentRecommendation {
  /** Stable id of the finding (kind + subject + window); decisions are remembered per fingerprint. */
  fingerprint: string;
  kind: AgentRecommendationKind;
  subject: { agent_profile_id: string | null; agent_profile_version_id: string | null; area: string | null; learning_id: string | null };
  rationale: string;
  evidence: Record<string, number | string>;
  status: "open" | "accepted" | "dismissed";
  decided_by: string | null;
  decided_at: string | null;
}

export const decideRecommendationSchema = z
  .object({ fingerprint: z.string().min(8).max(200), decision: z.enum(["accept", "dismiss"]), note: z.string().max(1_000).optional() })
  .strict();
export type DecideRecommendationInput = z.infer<typeof decideRecommendationSchema>;

export type { AgentRole, AgentRuntime, LearningKind, LearningStatus, MemoryScope };
